// useSelectedOrganization.js
import { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';

/**
 * Check if a string is a valid MongoDB ObjectId (24 character hex string)
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid MongoDB ObjectId format
 */
const isValidMongoId = (id) => {
  if (!id || typeof id !== 'string') return false;
  return /^[a-fA-F0-9]{24}$/.test(id);
};

/**
 * Hook to get the currently selected organization.
 * For super_admin, returns the org from the switcher (localStorage).
 * For other users, returns their assigned organization.
 *
 * @param {string|number} userOrganizationId - The user's default organization ID
 * @returns {string} The active organization ID
 */
export const useSelectedOrganization = (userOrganizationIdParam) => {
  const { isSuperAdmin, userInfo } = useUser();
  const userOrganizationId = userOrganizationIdParam || userInfo?.organizationId;

  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    if (isSuperAdmin()) {
      const storedOrg = localStorage.getItem('selectedOrganization');
      // Only use stored value if it's a valid MongoDB ObjectId
      if (storedOrg && !isValidMongoId(storedOrg)) {
        localStorage.removeItem('selectedOrganization');
        return userOrganizationId || '';
      }
      return storedOrg || userOrganizationId || '';
    }
    return userOrganizationId || '';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      if (isSuperAdmin()) {
        const newOrg = localStorage.getItem('selectedOrganization') || '';
        // Only update if it's a valid MongoDB ObjectId
        if (newOrg && !isValidMongoId(newOrg)) {
          localStorage.removeItem('selectedOrganization');
          setSelectedOrganization(userOrganizationId || '');
          return;
        }
        setSelectedOrganization(newOrg || userOrganizationId || '');
      }
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isSuperAdmin, userOrganizationId]);

  // Update when userOrganizationId changes (for non-super-admin)
  useEffect(() => {
    if (!isSuperAdmin()) {
      setSelectedOrganization(userOrganizationId || '');
    }
  }, [userOrganizationId, isSuperAdmin]);

  // For super_admin, check localStorage on mount
  useEffect(() => {
    if (isSuperAdmin()) {
      const storedOrg = localStorage.getItem('selectedOrganization');
      if (storedOrg && isValidMongoId(storedOrg)) {
        setSelectedOrganization(storedOrg);
      } else if (storedOrg) {
        // Clear invalid (old PostgreSQL) ID
        localStorage.removeItem('selectedOrganization');
        setSelectedOrganization(userOrganizationId || '');
      } else if (userOrganizationId) {
        setSelectedOrganization(userOrganizationId);
      }
    }
  }, [isSuperAdmin, userOrganizationId]);

  return selectedOrganization;
};
